<template>
  <span>
    <v-tooltip right>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          id="file-upload-button"
          :disabled="pending"
          small
          text
          outlined
          @click.prevent="onUploadClick"
          color="#006AE3"
          v-bind="attrs"
          v-on="on"
          :style="pending ? 'border-color: gray' : 'border-color: #006ae3'"
        >
          <css-spinner v-if="pending" slotClass="ml-2 grey--text" light>
            {{
              processing
                ? "Processing..."
                : percentUploaded
                ? `Uploaded ${Math.ceil(percentUploaded)}%`
                : `Uploading...`
            }}
          </css-spinner>
          <span v-else>
            <v-icon small class="pr-1">mdi-cloud-upload-outline</v-icon>
            upload</span
          >
        </v-btn>
      </template>
      <div>
        Click to upload a Shapefile/GeoJSON. We accept files with the following
        extensions: .zip, .json, and .geojson
        <div class="mt-4">
          <upload-requirements />
        </div>
      </div>
    </v-tooltip>
    <v-tooltip right v-if="processing">
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          @click="
            () => {
              cancelProcessing = true;
              processing = false;
              pending = false;
            }
          "
          v-bind="attrs"
          v-on="on"
          small
          icon
          color="#006AE3"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
      Cancel
    </v-tooltip>
    <input
      id="file-upload"
      ref="fileInput"
      class="d-none"
      type="file"
      accept=".zip, .json, .geojson"
      :disabled="pending"
      @change="onFileChange"
      @click="
        /*
         * workaround to fix issue not
         * triggering onFileChange event
         * when the same file is
         * selected
         */
        $refs.fileInput.value = ''
      "
    />
    <confirm-modal
      ref="confirmModal"
      title="Continue Upload?"
      confirm-btn-text="Yes, Continue"
      cancel-btn-text="No, Cancel"
    >
      <div style="max-width: 350px">
        <span class="text--secondary">The file</span> {{ fileName }}
        <span class="text--secondary"
          >may take minutes to upload and process due to it's</span
        >
        {{ fileSize }}
        <span class="text--secondary"
          >file size. Would you like to continue?</span
        >
      </div>
    </confirm-modal>
  </span>
</template>

<script>
import Vue from "vue";
import ConfirmModal from "@/components/shared/ConfirmModal";
import CssSpinner from "@/components/shared/CssSpinner";
import { createNamespacedHelpers } from "vuex-composition-helpers";
import UploadRequirements from "./UploadRequirements.vue";
import { parseGeoJson, processShapefile } from "./UploadApi.js";
const uiStore = createNamespacedHelpers("ui");

export default {
  props: {
    light: { default: false },
  },
  components: { UploadRequirements, ConfirmModal, CssSpinner },
  data() {
    return {
      pending: false,
      processing: false,
      cancelProcessing: false,
      error: null,
      fileSize: null,
      fileName: null,
      percentUploaded: null,
      requireConfirmationSize: 5000000,
    };
  },
  setup() {
    const { SHOW_SNACKBAR } = uiStore.useMutations(["SHOW_SNACKBAR"]);
    return { SHOW_SNACKBAR };
  },
  methods: {
    onUploadClick() {
      this.$refs.fileInput.click();
    },
    handleGEOJsonFile(file) {
      return new Promise((resolve, reject) => {
        // eslint-disable-next-line
        const $vm = this;
        const reader = new FileReader();
        reader.onloadend = function () {
          Vue.prototype.$log.debug(`onloadend`);
          $vm.percentUploaded = null;
        };
        reader.onloadstart = function () {
          Vue.prototype.$log.debug(`onloadstart`);
          $vm.percentUploaded = 0;
        };
        reader.onprogress = function (event) {
          $vm.percentUploaded = Math.round((event.loaded / event.total) * 100);
          Vue.prototype.$log.debug(`onprogress ${$vm.percentUploaded}`);
        };
        reader.onload = function () {
          if (reader.readyState !== 2 || reader.error) {
            Vue.prototype.$log.error(
              "reader error",
              (reader && reader.error) || ""
            );
            return reject(reader);
          } else {
            $vm.processing = true;
            $vm.percentUploaded = null;

            /* wrap into a promise with set timeout so UI updates with processing set to true */
            return new Promise((resolve) => {
              setTimeout(async () => {
                try {
                  const json = parseGeoJson(reader.result);
                  return resolve(json);
                } catch (err) {
                  return reject(err);
                } finally {
                  $vm.processing = false;
                }
              }, 50);
            }).then((json) => resolve(json));
          }
        };
        reader.readAsArrayBuffer(file);
      });
    },
    handleZipFile(file) {
      return new Promise((resolve, reject) => {
        // eslint-disable-next-line
        const $vm = this;
        const reader = new FileReader();
        reader.onloadend = function () {
          Vue.prototype.$log.debug(`onloadend`);
          $vm.percentUploaded = null;
        };
        reader.onloadstart = function () {
          Vue.prototype.$log.debug(`onloadstart`);
          $vm.percentUploaded = 0;
        };
        reader.onprogress = function (event) {
          $vm.percentUploaded = Math.round((event.loaded / event.total) * 100);
          Vue.prototype.$log.debug(`onprogress ${$vm.percentUploaded}`);
        };
        reader.onload = async function () {
          if (reader.readyState !== 2 || reader.error) {
            Vue.prototype.$log.error(
              "reader error",
              (reader && reader.error) || ""
            );
            return reject(reader);
          } else {
            $vm.processing = true;
            $vm.percentUploaded = null;

            try {
              const geojson = await processShapefile(reader.result);
              return resolve(geojson);
            } catch (error) {
              return reject(error);
            } finally {
              $vm.processing = false;
            }
          }
        };

        reader.readAsArrayBuffer(file);
      });
    },
    handleFile(file) {
      return new Promise((resolve, reject) => {
        const fileExt = file.name.substr(
          file.name.lastIndexOf(".") + 1,
          file.name.length
        );
        Vue.prototype.$log.debug("File Extension --> " + fileExt);
        switch (fileExt) {
          case "json":
          case "geojson":
            return resolve(this.handleGEOJsonFile(file));

          case "zip":
            return resolve(this.handleZipFile(file));

          default:
            return reject(
              new Error(`Unsupported file format '${fileExt}' encountered.`)
            );
        }
      });
    },
    onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files;
      if (files && files.length > 0) {
        // ask if we really want to continue if it's larger than this.requireConfirmationSize
        const sizeInBytes = files[0].size;
        const fsExt = ["Bytes", "KB", "MB", "GB"];
        let fSize = sizeInBytes;
        let i = 0;
        while (fSize > 900) {
          fSize /= 1024;
          i++;
        }
        this.fileSize = `${Math.round(fSize * 100) / 100} ${fsExt[i]}`;
        this.fileName = files[0].name;

        if (sizeInBytes > this.requireConfirmationSize) {
          this.$refs.confirmModal.show().then((yes) => {
            if (yes) {
              this.submitFile(files[0]);
            }
          });
        } else {
          this.submitFile(files[0]);
        }
      }
    },
    submitFile(file) {
      this.pending = true;
      this.error = null;
      this.cancelProcessing = false;

      this.handleFile(file)
        .then((result) => {
          Vue.prototype.$log.debug(`processing shapefile=,`, file);
          if (!this.cancelProcessing) {
            this.$emit("shapefileUploaded", result, file);
          }
        })
        .catch((error) => {
          Vue.prototype.$log.debug(`upload error: ${JSON.stringify(error)}`);
          this.error =
            error && error.message ? error.message : JSON.stringify(error);

          this.SHOW_SNACKBAR({
            text: `Shapefile upload failed: ${this.error}`,
            color: "error",
          });
        })
        .finally(() => {
          this.pending = false;
        });
    },
  },
};
</script>
